import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  DEFAULT_LANGUAGE,
  DEFAULT_LOCALE,
  getLanguageDirection,
  getLanguageMarket,
  normalizeLanguage,
  loadLocale
} from '../locales';
import {
  localizeSeoHref,
  resolveLocaleLanguageForPath
} from '../utils/localeRouting';
import {
  resolveClientLanguagePreference,
  writeLocalePreferenceCookie
} from '../utils/localePreference.js';
import {
  applyThemeToDocument,
  DEFAULT_THEME,
  normalizeTheme
} from '../utils/theme.js';
import { sanitizePublicCreditOnlyContent } from '../utils/publicCreditOnlyContent.js';

export const PUBLIC_APP_LANGUAGE_EVENT = 'stocktag-language-changed';

const PublicAppContext = createContext({
  theme: DEFAULT_THEME,
  preferencesHydrated: false,
  language: DEFAULT_LANGUAGE,
  texts: DEFAULT_LOCALE,
  setTheme: () => {},
  setLanguage: () => {}
});

const DEFAULT_PUBLIC_THEME = DEFAULT_THEME;

const readStoredTheme = () => {
  try {
    return normalizeTheme(localStorage.getItem('theme'));
  } catch {
    return DEFAULT_PUBLIC_THEME;
  }
};

const detectPreferredLanguage = () => {
  try {
    const navLanguages = typeof navigator !== 'undefined'
      ? [navigator.language, ...(navigator.languages || [])]
      : [];
    const timezone = Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || '';
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    const preferredLanguage = resolveClientLanguagePreference({
      pathname,
      cookieHeader: typeof document !== 'undefined' ? document.cookie : '',
      storedLanguage: localStorage.getItem('ui_language') || '',
      browserLanguages: navLanguages,
      timeZone: timezone,
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      host: typeof window !== 'undefined' ? window.location.host : ''
    });

    return resolveLocaleLanguageForPath(pathname, preferredLanguage);
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

export const PublicAppProvider = ({ children, initialLanguage = null, initialTheme = null, initialTexts = null }) => {
  const resolvedInitialLanguage = normalizeLanguage(
    initialLanguage || (typeof window === 'undefined' ? DEFAULT_LANGUAGE : detectPreferredLanguage())
  );
  const [theme, setThemeState] = useState(() => {
    if (initialTheme) return normalizeTheme(initialTheme);
    return typeof window === 'undefined' ? DEFAULT_PUBLIC_THEME : readStoredTheme();
  });
  const [language, setLanguageState] = useState(resolvedInitialLanguage);
  const [texts, setTexts] = useState(() => sanitizePublicCreditOnlyContent(
    initialTexts || DEFAULT_LOCALE,
    resolvedInitialLanguage
  ));
  // Keep the first server and client render identical. Theme-specific product
  // captures are revealed only after the stored preference has been read, so a
  // visitor never sees a capture for the wrong theme during hydration.
  const [preferencesHydrated, setPreferencesHydrated] = useState(false);

  const setTheme = useCallback((value) => {
    setThemeState(normalizeTheme(value));
  }, []);

  const setLanguage = useCallback((value) => {
    setLanguageState(normalizeLanguage(value));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    setThemeState((current) => {
      const nextTheme = readStoredTheme();
      return current === nextTheme ? current : nextTheme;
    });
    setLanguageState((current) => {
      const nextLanguage = detectPreferredLanguage();
      return current === nextLanguage ? current : nextLanguage;
    });
    setPreferencesHydrated(true);
  }, []);

  useEffect(() => {
    const nextTheme = applyThemeToDocument(theme);
    if (nextTheme !== theme) {
      setThemeState(nextTheme);
    }

    if (!preferencesHydrated) return;

    try {
      localStorage.setItem('theme', nextTheme);
    } catch {
      // noop
    }
  }, [preferencesHydrated, theme]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', getLanguageMarket(language).localeTag);
      document.documentElement.setAttribute('dir', getLanguageDirection(language));
    }

    if (!preferencesHydrated) return;

    try {
      localStorage.setItem('ui_language', language);
      writeLocalePreferenceCookie(language);
      window.dispatchEvent(new CustomEvent(PUBLIC_APP_LANGUAGE_EVENT, {
        detail: { language }
      }));
    } catch {
      // noop
    }
  }, [language, preferencesHydrated]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!preferencesHydrated) return;

    const pathname = window.location.pathname === '/index.html' ? '/' : window.location.pathname;
    // Same rule as the entry middleware: only move to a localized URL that is
    // prerendered, so a language change on an English-only blog post (a BlogBot
    // post or a missing translation) keeps the English URL instead of a 404.
    const urlLanguage = resolveLocaleLanguageForPath(pathname, language);
    const nextPath = localizeSeoHref(`${pathname}${window.location.search}${window.location.hash}`, urlLanguage);
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (nextPath !== currentPath) {
      window.history.replaceState({}, '', nextPath);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [language, preferencesHydrated]);

  useEffect(() => {
    let cancelled = false;

    const hydrateLocale = async () => {
      const nextLocale = await loadLocale(language);
      if (!cancelled) {
        setTexts(sanitizePublicCreditOnlyContent(nextLocale, language));
      }
    };

    hydrateLocale();

    return () => {
      cancelled = true;
    };
  }, [language]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleStorage = (event) => {
      if (event.key === 'ui_language') {
        const nextLanguage = normalizeLanguage(event.newValue || '');
        setLanguageState((current) => (current === nextLanguage ? current : nextLanguage));
        return;
      }

      if (event.key === 'theme') {
        const nextTheme = normalizeTheme(event.newValue);
        setThemeState((current) => (current === nextTheme ? current : nextTheme));
      }
    };

    const handleLanguageEvent = (event) => {
      const nextLanguage = normalizeLanguage(event?.detail?.language || '');
      setLanguageState((current) => (current === nextLanguage ? current : nextLanguage));
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(PUBLIC_APP_LANGUAGE_EVENT, handleLanguageEvent);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(PUBLIC_APP_LANGUAGE_EVENT, handleLanguageEvent);
    };
  }, []);

  const value = useMemo(() => ({
    theme,
    preferencesHydrated,
    setTheme,
    language,
    setLanguage,
    texts
  }), [language, preferencesHydrated, setLanguage, setTheme, texts, theme]);

  return (
    <PublicAppContext.Provider value={value}>
      {children}
    </PublicAppContext.Provider>
  );
};

export const usePublicAppContext = () => useContext(PublicAppContext);

export default PublicAppContext;
